import React from 'react';
import { Link } from 'react-router-dom';
import { BeakerIcon, ChartBarIcon, DocumentTextIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const LandingPage = () => {
  const features = [
    {
      icon: <BeakerIcon className="w-12 h-12 text-medical-600" />,
      title: 'Análise Inteligente',
      description: 'Sistema com IA que auxilia na organização e análise de dados audiométricos, seguindo as diretrizes da NR-7 e Portaria nº 19/98.',
    },
    {
      icon: <ChartBarIcon className="w-12 h-12 text-health-600" />,
      title: 'Histórico Completo',
      description: 'Gestão longitudinal de exames audiométricos com apoio à análise de evolução e acompanhamento administrativo.',
    },
    {
      icon: <DocumentTextIcon className="w-12 h-12 text-tech-600" />,
      title: 'Relatórios Padronizados',
      description: 'Geração de relatórios técnicos padronizados com base nas informações inseridas, para apoio à documentação gerencial.',
    },
    {
      icon: <ShieldCheckIcon className="w-12 h-12 text-medical-600" />,
      title: 'Padrões de Referência',
      description: 'Desenvolvido com base nas diretrizes da NR-7, Portaria nº 19/98 e legislação trabalhista vigente.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Navbar */}
      <nav className="fixed w-full bg-white/90 backdrop-blur-md shadow-sm z-50">
        <div className="container-custom">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img 
                src="/audfast-icon.png" 
                alt="AudFast" 
                className="w-[5.5rem] h-[5.5rem] object-contain"
              />
              <span className="text-2xl font-bold text-gradient">AudFast</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-medical-600 transition-colors">
                Recursos
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-medical-600 transition-colors">
                Preços
              </a>
              <Link to="/contact" className="text-gray-600 hover:text-medical-600 transition-colors">
                Contato
              </Link>
              <Link to="/support" className="text-gray-600 hover:text-medical-600 transition-colors">
                Suporte
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-medical-600 hover:text-medical-700 font-semibold transition-colors">
                Login
              </Link>
              <Link to="/dashboard" className="btn-primary">
                Entrar no Sistema
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Sua Plataforma de{' '}
                <span className="text-gradient-hero">
                  Audiometria Ocupacional
                </span>{' '}
                com IA
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Plataforma de gestão audiométrica com inteligência artificial desenvolvida para auxiliar na organização, análise administrativa e gestão de informações audiométricas.
              </p>
              <p className="text-base text-gray-500 mb-8 leading-relaxed">
                A solução tem caráter exclusivamente informativo e gerencial, oferecendo apoio à análise de dados e à padronização de relatórios conforme as diretrizes da NR-7 e da Portaria nº 19/98.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/dashboard" className="btn-primary text-center">
                  Começar Gratuitamente →
                </Link>
                <a href="#features" className="btn-secondary text-center">
                  Ver Demonstração
                </a>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                ✓ Sem cartão de crédito &nbsp;&nbsp;&nbsp; ✓ Setup em 2 minutos &nbsp;&nbsp;&nbsp; ✓ Suporte completo
              </p>
            </div>

            <div className="animate-slide-up animation-delay-200">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-medical-400 to-tech-400 rounded-2xl blur-3xl opacity-20 animate-pulse-slow"></div>
                <div className="relative bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 p-4 bg-gradient-to-r from-health-50 to-medical-50 rounded-lg">
                      <div className="w-10 h-10 bg-health-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">✓</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Gestão Completa</p>
                        <p className="text-sm text-gray-600">Organização com IA</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 p-4 bg-gradient-to-r from-medical-50 to-tech-50 rounded-lg">
                      <div className="w-10 h-10 bg-medical-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">✓</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Relatórios Padronizados</p>
                        <p className="text-sm text-gray-600">Apoio à documentação</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 p-4 bg-gradient-to-r from-tech-50 to-health-50 rounded-lg">
                      <div className="w-10 h-10 bg-tech-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">✓</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Análise de Dados</p>
                        <p className="text-sm text-gray-600">Suporte administrativo</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Recursos <span className="text-gradient">Premium</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Tecnologia de ponta para gestão audiométrica ocupacional 
              com inteligência artificial
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="card-gradient animate-slide-up hover:scale-105"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-medical-600 via-tech-600 to-health-600">
        <div className="container-custom text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Pronto para Modernizar sua Gestão Audiométrica?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Descubra como a tecnologia pode auxiliar na organização e padronização 
            de seus processos de gestão audiométrica ocupacional
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dashboard" className="btn-outline">
              Começar Teste Gratuito →
            </Link>
            <Link to="/contact" className="btn-secondary">
              Falar com Especialista
            </Link>
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              🛡️ Segurança e Conformidade
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nossa Inteligência Artificial está hospedada em um ambiente Enterprise, 
              que segue os mais altos padrões internacionais de segurança e privacidade.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Criptografia */}
            <div className="card-gradient">
              <div className="text-4xl mb-4">🔒</div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Criptografia</h3>
              <p className="text-gray-600 leading-relaxed">
                Utilizamos criptografia ponta a ponta, com TLS 1.2+ em trânsito e AES-256 em repouso, 
                garantindo proteção total dos dados em todas as etapas.
              </p>
            </div>

            {/* Privacidade */}
            <div className="card-gradient">
              <div className="text-4xl mb-4">🔐</div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Privacidade</h3>
              <p className="text-gray-600 leading-relaxed">
                A privacidade dos clientes é prioridade — os dados não são utilizados para treinar 
                modelos da OpenAI, assegurando confidencialidade e controle total sobre as informações.
              </p>
            </div>

            {/* Autenticação Corporativa */}
            <div className="card-gradient">
              <div className="text-4xl mb-4">👥</div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Autenticação Corporativa</h3>
              <p className="text-gray-600 leading-relaxed">
                Suporte a SSO/SAML, provisionamento via SCIM e controle de acesso baseado em função (RBAC), 
                oferecendo autenticação segura e gestão de permissões robusta.
              </p>
            </div>

            {/* Gestão Centralizada */}
            <div className="card-gradient">
              <div className="text-4xl mb-4">🧭</div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Gestão Centralizada</h3>
              <p className="text-gray-600 leading-relaxed">
                Ferramentas de verificação de domínio, console administrativo e painel de monitoramento 
                de uso permitem visibilidade e controle completos sobre o ambiente.
              </p>
            </div>

            {/* Conformidade e Certificações */}
            <div className="card-gradient">
              <div className="text-4xl mb-4">📜</div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Conformidade e Certificações</h3>
              <div className="text-gray-600 leading-relaxed">
                <p className="mb-3">Ambiente certificado com:</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>SOC 2 Tipo II</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>ISO 27001, ISO 27017, ISO 27018 e ISO 27701</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Suporte às normas GDPR/CCPA</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Opções de residência de dados</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Regulação Europeia */}
            <div className="card-gradient">
              <div className="text-4xl mb-4">🌍</div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Regulação Europeia</h3>
              <p className="text-gray-600 leading-relaxed">
                A infraestrutura está alinhada às exigências da Diretiva NIS (Network and Information Systems), 
                aplicáveis nos países em que opera.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container-custom">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="/audfast-icon.png" 
                  alt="AudFast" 
                  className="w-[5rem] h-[5rem] object-contain"
                />
                <span className="text-2xl font-bold text-white">AudFast</span>
              </div>
              <p className="text-gray-400 text-sm">
                Plataforma de gestão audiométrica ocupacional com inteligência artificial para apoio administrativo e gerencial.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Produto</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Recursos</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Preços</a></li>
                <li><Link to="/dashboard" className="hover:text-white transition-colors">Demo</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Suporte</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/support" className="hover:text-white transition-colors">Central de Ajuda</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contato</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/data-deletion" className="hover:text-white transition-colors">Data Deletion</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-xs text-gray-500 mb-4 max-w-4xl mx-auto leading-relaxed">
              <strong>Aviso Legal:</strong> O AudFast é uma ferramenta de apoio administrativo e gerencial. 
              Não substitui a avaliação clínica profissional, o julgamento médico ou a responsabilidade 
              do profissional de saúde. Todos os relatórios e análises gerados devem ser revisados e 
              validados por profissional qualificado antes de qualquer uso clínico ou legal.
            </p>
            <p className="text-sm text-gray-400">© 2025 AudFast. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
